
import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  ShoppingBag, 
  Tag, 
  CheckCircle, 
  Truck, 
  Package, 
  Clock 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuGroup, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Notification {
  id: string;
  user_id: string;
  type: 'purchase' | 'sale' | 'delivery' | 'confirmation';
  message: string;
  order_id: string | null;
  created_at: string;
  read: boolean;
}

const NotificationDropdown = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { profile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    // Skip fetching if not authenticated
    if (!isAuthenticated || !profile?.id) return;
    
    // Initial fetch of notifications
    const fetchNotifications = async () => {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(10) as { data: Notification[] | null, error: any };
        
        if (error) {
          console.error('Error fetching notifications:', error);
          return;
        }
        
        setNotifications(data || []);
      } catch (error) {
        console.error('Exception in fetchNotifications:', error);
      }
    };
    
    fetchNotifications();
    
    // Set up real-time subscription for notifications
    const channel = supabase
      .channel('notification-changes')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'notifications',
        filter: `user_id=eq.${profile.id}`
      }, (payload) => {
        // Add new notification to state
        setNotifications(prev => [{
          id: payload.new.id,
          user_id: payload.new.user_id,
          type: payload.new.type as 'purchase' | 'sale' | 'delivery' | 'confirmation',
          message: payload.new.message,
          order_id: payload.new.order_id,
          created_at: payload.new.created_at,
          read: payload.new.read
        } as Notification, ...prev]);
        
        // Show toast for new notification
        toast({
          title: "Njoftim i ri",
          description: payload.new.message,
          duration: 5000,
        });
      })
      .subscribe();
    
    return () => {
      // Important: properly cleanup the subscription
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, profile?.id]); // Only depend on these two values
  
  const markAsRead = async (notificationId: string) => {
    // Check auth to prevent unnecessary calls
    if (!isAuthenticated || !profile?.id) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId) as { error: any };
      
      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
    } catch (error) {
      console.error('Exception in markAsRead:', error);
    }
  };
  
  const handleNotificationClick = async (notification: Notification) => {
    // Mark notification as read
    await markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.type === 'purchase') {
      navigate('/profile');
      // Open the purchases dialog
      setTimeout(() => {
        const purchasesButton = document.querySelector('[data-purchases-button="true"]');
        if (purchasesButton) {
          (purchasesButton as HTMLButtonElement).click();
        }
      }, 300);
    } else if (notification.type === 'sale') {
      navigate('/profile');
      // Open the sales dialog
      setTimeout(() => {
        const salesButton = document.querySelector('[data-sales-button="true"]');
        if (salesButton) {
          (salesButton as HTMLButtonElement).click();
        }
      }, 300);
    }
    
    setIsOpen(false);
  };
  
  const markAllAsRead = async () => {
    if (!isAuthenticated || !profile?.id) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', profile.id)
        .eq('read', false) as { error: any };
      
      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Exception in markAllAsRead:', error);
    }
  };
  
  // Get number of unread notifications
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Don't show anything if not authenticated
  if (!isAuthenticated) return null;
  
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'purchase':
        return <ShoppingBag size={16} className="text-blue-500" />;
      case 'sale':
        return <Tag size={16} className="text-green-500" />;
      case 'delivery':
        return <Truck size={16} className="text-yellow-500" />;
      case 'confirmation':
        return <CheckCircle size={16} className="text-purple-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };
  
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    
    if (diffMin < 1) return 'Tani';
    if (diffMin < 60) return `${diffMin} min më parë`;
    
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} orë më parë`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} ditë më parë`;
    
    return date.toLocaleDateString('sq-AL');
  };
  
  return (
    <div className="relative">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 relative">
            <Bell size={20} />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuLabel className="flex justify-between items-center">
            <span>Njoftimet</span>
            {unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                Shëno të gjitha si të lexuara
              </button>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuGroup className="max-h-[300px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-4 text-center text-gray-500">
                Nuk keni njoftime
              </div>
            ) : (
              notifications.map(notification => (
                <DropdownMenuItem 
                  key={notification.id}
                  className={`flex items-start p-3 cursor-pointer ${!notification.read ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="mr-3 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                  )}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default NotificationDropdown;
